import { useCallback, useEffect, useRef, useState } from 'react';
import ApiService from '../services/api';
import type { BlogArticle, BlogArticlePagination } from '../constants/blogArticles';

interface UsePublicArticlesOptions {
  featured?: boolean;
  limit?: number;
  page?: number;
  category?: string;
  search?: string;
  enabled?: boolean;
}

interface UsePublicArticlesResult {
  articles: BlogArticle[];
  pagination: BlogArticlePagination | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function usePublicArticles(options: UsePublicArticlesOptions = {}): UsePublicArticlesResult {
  const {
    featured,
    limit = 12,
    page = 1,
    category,
    search,
    enabled = true
  } = options;

  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [pagination, setPagination] = useState<BlogArticlePagination | null>(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState<string | null>(null);

  const requestId = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setArticles([]);
      setPagination(null);
      setLoading(false);
      return;
    }

    const current = requestId.current + 1;
    requestId.current = current;
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getPublicBlogArticles({
        featured,
        limit,
        page,
        category,
        search
      }) as {
        success?: boolean;
        data?: {
          articles?: BlogArticle[];
          pagination?: BlogArticlePagination;
        };
      };
      if (requestId.current !== current) return;

      setArticles(response?.data?.articles || []);
      setPagination(response?.data?.pagination || null);
    } catch (err) {
      if (requestId.current !== current) return;
      setArticles([]);
      setPagination(null);
      setError(err instanceof Error ? err.message : 'Failed to load articles');
    } finally {
      if (requestId.current === current) {
        setLoading(false);
      }
    }
  }, [category, enabled, featured, limit, page, search]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    articles,
    pagination,
    loading,
    error,
    refresh
  };
}

export function usePublicArticleBySlug(slug?: string) {
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [loading, setLoading] = useState(Boolean(slug));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setArticle(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await ApiService.getPublicBlogArticleBySlug(slug) as {
          success?: boolean;
          data?: BlogArticle;
        };
        if (!cancelled) {
          setArticle(response?.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          setArticle(null);
          setError(err instanceof Error ? err.message : 'Failed to load article');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { article, loading, error };
}
